import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import SeverityBadge from "../components/SeverityBadge";
import { Button } from "../components/ui/button";
import { formatIDR, formatPercent, formatDate } from "../lib/format";
import { ArrowLeft, GitBranch } from "lucide-react";

export default function GroupObligorDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/group-obligors/${id}`).then((r) => setData(r.data));
  }, [id]);

  if (!data) return <div className="p-8 text-sm text-muted-foreground">Memuat…</div>;
  const g = data.group;
  const ratio = data.summary.total_exposure / (g.group_limit || 1);

  return (
    <div className="p-8 max-w-[1400px] mx-auto" data-testid="group-detail-page">
      <Button variant="ghost" size="sm" onClick={() => navigate("/groups")} className="mb-4">
        <ArrowLeft className="h-3.5 w-3.5 mr-1" /> Kembali
      </Button>

      <PageHeader
        title={g.group_name}
        subtitle={`Group ID: ${g.group_id}`}
        action={data.summary.breach ? <SeverityBadge severity="critical">GROUP LIMIT BREACH</SeverityBadge> : null}
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="kpi-card">
          <div className="kpi-label">Group Limit</div>
          <div className="kpi-value text-2xl">{formatIDR(g.group_limit, { compact: true })}</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Total Approved</div>
          <div className="kpi-value text-2xl">{formatIDR(data.summary.total_approved_limit, { compact: true })}</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Total Eksposur</div>
          <div className={`kpi-value text-2xl ${data.summary.breach ? "text-[#D0021B]" : ""}`}>
            {formatIDR(data.summary.total_exposure, { compact: true })}
          </div>
          <div className={`kpi-sub mono ${data.summary.breach ? "text-[#D0021B]" : ""}`}>{formatPercent(ratio)} dari group limit</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Available</div>
          <div className="kpi-value text-2xl">{formatIDR(data.summary.available_limit, { compact: true })}</div>
        </div>
      </div>

      <div className="border border-border rounded-md bg-card overflow-hidden mb-6">
        <div className="px-4 py-3 border-b border-border">
          <h3 className="text-sm font-bold uppercase tracking-wider">Anggota Group ({data.members.length})</h3>
        </div>
        <table className="data-table">
          <thead>
            <tr>
              <th>Borrower ID</th>
              <th>Nama</th>
              <th>Tipe</th>
              <th>Sektor</th>
              <th>Risk Rating</th>
            </tr>
          </thead>
          <tbody>
            {data.members.map((m) => (
              <tr key={m.borrower_id} className="cursor-pointer" onClick={() => navigate(`/borrowers/${m.borrower_id}`)}>
                <td className="mono">{m.borrower_id}</td>
                <td className="font-medium">{m.name}</td>
                <td>{m.borrower_type}</td>
                <td>{m.sector}</td>
                <td className="mono">{m.risk_rating}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="border border-border rounded-md bg-card overflow-hidden">
        <div className="px-4 py-3 border-b border-border">
          <h3 className="text-sm font-bold uppercase tracking-wider">Fasilitas Group ({data.facilities.length})</h3>
        </div>
        <table className="data-table">
          <thead>
            <tr>
              <th>Facility</th>
              <th>Borrower</th>
              <th>Tipe</th>
              <th className="text-right">Approved</th>
              <th className="text-right">Utilized</th>
              <th>Jatuh Tempo</th>
            </tr>
          </thead>
          <tbody>
            {data.facilities.map((f) => (
              <tr key={f.facility_id} className="cursor-pointer" onClick={() => navigate(`/facilities/${f.facility_id}`)}>
                <td className="mono">{f.facility_id}</td>
                <td className="text-xs mono">{f.borrower_id}</td>
                <td>{f.facility_type}</td>
                <td className="text-right-num">{formatIDR(f.approved_limit, { compact: true })}</td>
                <td className="text-right-num">{formatIDR(f.utilized_limit, { compact: true })}</td>
                <td className="mono text-xs">{formatDate(f.maturity_date)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
